import { MixpanelEvent, MixpanelEventProperty, useDisclosureWithContext, useMixpanel } from '@talos/kyoko';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { LPSettlementBatchMessageData } from './generated/lpSettlementTypes';
import { UnbatchConfirmationDialog, type UnbatchDialogContext } from './UnbatchConfirmationDialog';

interface LPSettlementContextType {
  openUnbatchDialog: (settlementBatch: LPSettlementBatchMessageData, onClose?: () => void) => void;
  showAllDecimals: boolean;
  setShowAllDecimals: (value: boolean) => void;
}

const LPSettlementContext = createContext<LPSettlementContextType | undefined>(undefined);

interface LPSettlementProviderProps {
  children: React.ReactNode;
}

export function LPSettlementProvider({ children }: LPSettlementProviderProps) {
  const mixpanel = useMixpanel();
  const unbatchDialog = useDisclosureWithContext<UnbatchDialogContext>();
  const [showAllDecimals, setShowAllDecimals] = useState(false);

  const openUnbatchDialog = useCallback(
    (settlementBatch: LPSettlementBatchMessageData, onClose?: () => void) => {
      unbatchDialog.open({
        settlementBatch,
        onClose,
      });
    },
    [unbatchDialog]
  );

  const handleSetShowAllDecimals = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.ShowAllDecimals, {
        [MixpanelEventProperty.Value]: value,
      });
      setShowAllDecimals(value);
    },
    [mixpanel]
  );

  const value = useMemo(
    () => ({
      openUnbatchDialog,
      showAllDecimals,
      setShowAllDecimals: handleSetShowAllDecimals,
    }),
    [openUnbatchDialog, showAllDecimals, handleSetShowAllDecimals]
  );

  return (
    <LPSettlementContext.Provider value={value}>
      {children}
      {unbatchDialog.context && (
        <UnbatchConfirmationDialog
          isOpen={unbatchDialog.isOpen}
          close={unbatchDialog.close}
          context={unbatchDialog.context}
        />
      )}
    </LPSettlementContext.Provider>
  );
}

export function useLPSettlementContext() {
  const context = useContext(LPSettlementContext);
  if (context === undefined) {
    throw new Error('useLPSettlementContext must be used within LPSettlementProvider');
  }
  return context;
}
